import { useState } from 'react';
import { Link } from '@g2u/ui-kit';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getPrepaidProducts, putCustomerProduct, PREPAID_PRODUCTS_QUERY_KEY } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem, PrepaidProductStatus, PrepaidProductStatusLabels } from 'types';

const PREPAID_PRODUCTS_PER_PAGE = 5;

export const useCustomerProducts = (customerId?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const { data, isLoading: isListLoading } = useQuery([PREPAID_PRODUCTS_QUERY_KEY, customerId, page], getPrepaidProducts, {
    enabled: !!customerId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate, isLoading: isProductsTransfering } = useMutation(putCustomerProduct, {
    onSuccess: (_, variables) => {
      const customerUrl = `/customer/edit/${variables.customerId}`;
      const toastMsg = variables.status ? (
        <>Product has been marked as {PrepaidProductStatusLabels[variables.status].toLowerCase()}</>
      ) : (
        <>
          Product has been transferred to another customer account{' '}
          {variables.name && (
            <>
              - <Link.$ onClick={() => navigate(`${customerUrl}/details`)}>{variables.name}</Link.$>
            </>
          )}
        </>
      );

      Toast.show({
        description: toastMsg,
        type: Toast.Type.Success,
      });

      // If mutate has been passed with param `status` consider the putCustomerProduct has been intended to use for updating customer product status
      // else consider it is for transfer
      variables.status ? queryClient.invalidateQueries([PREPAID_PRODUCTS_QUERY_KEY]) : navigate(`${customerUrl}/discounts`);
    },
    onError: (error) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const transferProducts = (customerProductId: number, customerId: number, name: string) => {
    mutate({ customerProductId, customerId, name });
  };

  const updateCustomerProductStatus = (customerProductId: number, status: PrepaidProductStatus) => {
    mutate({ customerProductId, status });
  };

  const paginationMetadata = {
    total: data?.total ?? 0,
    current_page: data?.current_page ?? page,
    pageSize: PREPAID_PRODUCTS_PER_PAGE,
    onChange: (nextPage: number) => setPage(nextPage),
  };

  return {
    items: data?.data,
    paginationMetadata,
    setPage,
    transferProducts,
    updateCustomerProductStatus,
    isLoading: isListLoading || isProductsTransfering,
  };
};
